/**
 * PipelineSummaryRow — the always-visible ~52px line for one pipeline.
 *
 * A board of 34 pipelines was 34 full editor cards; scanning it meant scrolling
 * past three dropdowns per row. The summary line carries only what you scan for
 * — name, key, state, and the model that is LIVE right now — and clicking it
 * (or its chevron) expands the same editor inline.
 *
 * The whole line is the disclosure control (role=button, Enter/Space), so the
 * hit target is the row, not a 24px icon.
 *
 * The warning dot is load-bearing: a row with an off-catalog or deprecated live
 * pointer must announce that while COLLAPSED, otherwise the redesign would hide
 * exactly the rows an operator has to act on.
 */

import React from 'react';
import { Box, Typography, Chip, Tooltip } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LockIcon from '@mui/icons-material/Lock';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import { colors, typography } from '../../../theme/designTokens';
import { PipelineModel } from '../../../services/quillAdminService';
import { PipelineRowModel } from './pipelineRowModel';
import { CounterbalanceChip, conflictsForPipeline } from './CounterbalanceNotice';

export interface PipelineSummaryRowProps {
  pipeline: PipelineModel;
  model: PipelineRowModel;
  expanded: boolean;
  onToggle: () => void;
}

export const PipelineSummaryRow: React.FC<PipelineSummaryRowProps> = ({
  pipeline,
  model,
  expanded,
  onToggle,
}) => {
  const { isFixed, isSafety, active, hasProblem, problemNote } = model;

  // A collapsed counterbalance must announce itself while the row is COLLAPSED,
  // for the same reason the warning dot does: the redesign must not hide the
  // rows an operator has to look at.
  const counterbalanceConflicts = React.useMemo(() => conflictsForPipeline(pipeline), [pipeline]);

  // ONE text node on purpose: "xai · grok-4.3" reads as a single identity and
  // never collides with the expanded editor's own model_id rendering.
  const activeLabel = `${active.provider} · ${active.model_id}`;

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-expanded={expanded}
      onClick={onToggle}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggle();
        }
      }}
      sx={{
        minHeight: 52,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.5,
        py: 1,
        cursor: 'pointer',
        userSelect: 'none',
        borderRadius: '16px',
        '&:hover': { backgroundColor: colors.gray[50] },
        '&:focus-visible': { outline: `2px solid ${colors.deepTeal[400]}`, outlineOffset: -2 },
      }}
    >
      {expanded ? (
        <ExpandLessIcon sx={{ fontSize: 20, color: 'text.secondary', flexShrink: 0 }} />
      ) : (
        <ExpandMoreIcon sx={{ fontSize: 20, color: 'text.secondary', flexShrink: 0 }} />
      )}

      <Typography
        sx={{
          fontWeight: 700,
          // designTokens has no `md` step — the old card used typography.sizes.md,
          // which resolved to undefined. `base` is the 16px it meant.
          fontSize: typography.sizes.base,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          flexShrink: 0,
          maxWidth: { xs: 160, sm: 260 },
        }}
      >
        {pipeline.display_name}
      </Typography>

      <Tooltip title={pipeline.pipeline_key} arrow>
        <Typography
          variant="caption"
          sx={{
            fontFamily: typography.fonts.mono,
            color: 'text.disabled',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: { xs: 90, sm: 180 },
            display: { xs: 'none', sm: 'block' },
          }}
        >
          {pipeline.pipeline_key}
        </Typography>
      </Tooltip>

      {hasProblem && (
        <Tooltip title={problemNote} arrow>
          <Box
            aria-label="Needs attention"
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: colors.warning.main,
              flexShrink: 0,
              cursor: 'help',
            }}
          />
        </Tooltip>
      )}

      <Box sx={{ flexGrow: 1, minWidth: 8 }} />

      <CounterbalanceChip conflicts={counterbalanceConflicts} />

      {isSafety && (
        <Tooltip title="Safety-path pipeline — model changes require a vulnerable-user re-verification (G3)." arrow>
          <Chip
            icon={<HealthAndSafetyIcon sx={{ fontSize: 14 }} />}
            label="safety"
            size="small"
            color="error"
            variant="outlined"
            sx={{ cursor: 'help', flexShrink: 0 }}
          />
        </Tooltip>
      )}

      {isFixed ? (
        <Chip
          icon={<LockIcon sx={{ fontSize: 14 }} />}
          label="fixed"
          size="small"
          sx={{ flexShrink: 0 }}
        />
      ) : pipeline.is_on_default ? (
        <Chip
          label="on default"
          size="small"
          sx={{
            fontWeight: 600,
            flexShrink: 0,
            color: colors.info.dark,
            backgroundColor: colors.info[50],
            border: `1px solid ${colors.info.light}`,
          }}
        />
      ) : (
        <Chip
          label="overridden"
          size="small"
          sx={{
            fontWeight: 600,
            flexShrink: 0,
            color: colors.warning.dark,
            backgroundColor: colors.warning[50],
            border: `1px solid ${colors.warning.light}`,
          }}
        />
      )}

      <Typography
        sx={{
          fontFamily: typography.fonts.mono,
          fontSize: typography.sizes.xs,
          color: colors.gray[700],
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: { xs: 120, sm: 240 },
          flexShrink: 0,
        }}
      >
        {activeLabel}
      </Typography>
    </Box>
  );
};

export default PipelineSummaryRow;
